import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DocumentSettingsGQL, DocumentSettingsPeriodType, DocumentSettingsQuery, SetConfigValuesGQL } from 'graphql.generated';
import { debounceTime, distinctUntilChanged, finalize, skip } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SubSink } from 'subsink';

import { PeriodService } from '../../dashboard/period.service';
import { AdvancedPeriodOptions } from '../../reports/reports.constants';
import { BrandingService } from '../../services/branding.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';

type EditableConfigField = 'templateId' | 'templateUrl';
type DocumentEngine = 'aegis' | 'pandadoc';

interface AegisTemplate {
  name: string;
  description: string;
  icon: string;
  url: string;
}

const PROD_FUNCTIONS_BASE_URL = 'https://functions.moveright.app';

function functionsBaseUrl() {
  return environment.production ? PROD_FUNCTIONS_BASE_URL : `${window.location.origin}/functions`;
}

@Component({
  selector: 'app-document-settings',
  templateUrl: './document-settings.component.html',
  styleUrls: ['./document-settings.component.scss'],
})
export class DocumentSettingsComponent implements OnInit, OnDestroy {
  private readonly subs = new SubSink();
  private requestId = 0;

  rows: DocumentSettingsQuery['documentSettings'] = [];
  loading = false;
  editing?: { configKey: string; field: EditableConfigField };
  editableValue = '';
  aegisTemplates: AegisTemplate[] = [];
  filteredAegisTemplates: string[] = [];
  saving = false;
  updatingEngineKeys = new Set<string>();

  readonly periodTypes = [
    { label: 'Document created at', value: DocumentSettingsPeriodType.CreatedAt },
    { label: 'Document updated at', value: DocumentSettingsPeriodType.UpdatedAt },
  ];

  readonly periods = this.periodService.getPeriodNameValues({
    includeCustom: true,
    defaultPeriod: 'This Month',
  }).filter((period) => !['Future'].includes(period.name));

  readonly filterForm = new FormGroup({
    periodType: new FormControl<DocumentSettingsPeriodType>(DocumentSettingsPeriodType.CreatedAt, { nonNullable: true }),
    period: new FormControl<AdvancedPeriodOptions>('This Month', { nonNullable: true }),
    customPeriod: new FormControl<Date[]>([], { nonNullable: true, validators: Validators.required }),
  });

  constructor(
    private documentSettingsGQL: DocumentSettingsGQL,
    private setConfigValuesGQL: SetConfigValuesGQL,
    private periodService: PeriodService,
    private brandingService: BrandingService,
    private notify: FreyaNotificationsService,
    private http: HttpClient,
  ) {}

  ngOnInit() {
    this.setDatesFromPeriod('This Month');
    this.subs.sink = this.filterForm.controls.period.valueChanges.subscribe((period) => {
      if (period !== 'Custom') {
        this.setDatesFromPeriod(period);
      }
    });
    this.subs.sink = this.filterForm.valueChanges.pipe(debounceTime(150)).subscribe(() => this.load());
    this.subs.sink = this.brandingService.currentZone().pipe(
      distinctUntilChanged((previous, current) => previous?.id === current?.id),
      skip(1),
    ).subscribe(() => this.load());
    this.loadAegisTemplates();
    this.load();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  load() {
    const [start, end] = this.filterForm.controls.customPeriod.value;
    if (!start || !end) { return; }

    const requestId = ++this.requestId;
    this.loading = true;
    this.subs.sink = this.documentSettingsGQL.fetch({
      period: {
        start: Math.floor(start.getTime() / 1000),
        end: Math.floor(end.getTime() / 1000),
        type: this.filterForm.controls.periodType.value,
      },
    }, { fetchPolicy: 'network-only' }).pipe(finalize(() => {
      if (requestId === this.requestId) {
        this.loading = false;
      }
    })).subscribe(({ data }) => {
      if (requestId !== this.requestId) { return; }
      this.rows = data.documentSettings;
    });
  }

  startEditing(row: DocumentSettingsQuery['documentSettings'][number], field: EditableConfigField) {
    this.editing = { configKey: row.config.key, field };
    this.editableValue = field === 'templateId'
      ? row.pandadocTemplateId || ''
      : row.aegisTemplateUrl || '';
  }

  isEditing(configKey: string, field: EditableConfigField) {
    return this.editing?.configKey === configKey && this.editing.field === field;
  }

  documentCount(row: DocumentSettingsQuery['documentSettings'][number], engine: string) {
    return row.documentCounts.find((documentCount) => documentCount.engine === engine)?.count || 0;
  }

  aegisMigrationRate(row: DocumentSettingsQuery['documentSettings'][number]) {
    return this.migrationRate(
      this.documentCount(row, 'aegis'),
      this.documentCount(row, 'pandadoc'),
    );
  }

  totalDocumentCount(engine: DocumentEngine) {
    return this.rows.reduce((total, row) => total + this.documentCount(row, engine), 0);
  }

  totalAegisMigrationRate() {
    return this.migrationRate(
      this.totalDocumentCount('aegis'),
      this.totalDocumentCount('pandadoc'),
    );
  }

  cancelEditing() {
    this.editing = undefined;
    this.editableValue = '';
  }

  filterAegisTemplates(event: { query: string }) {
    const query = event.query.trim().toLocaleLowerCase();
    this.filteredAegisTemplates = this.aegisTemplates
      .filter((template) => (
        template.name.toLocaleLowerCase().includes(query)
        || template.url.toLocaleLowerCase().includes(query)
      ))
      .map((template) => template.url);
  }

  aegisTemplateName(url?: string | null) {
    return url?.split('/').pop() || '—';
  }

  save(row: DocumentSettingsQuery['documentSettings'][number], field: EditableConfigField) {
    let configValue: Record<string, unknown>;
    try {
      configValue = JSON.parse(row.config.value || '') as Record<string, unknown>;
    } catch {
      this.notify.error('Failed to update document setting', 'The current configuration is not valid JSON.');
      return;
    }

    const savedValue = this.editableValue;
    configValue[field] = savedValue;
    const serializedValue = JSON.stringify(configValue);
    this.saving = true;
    this.subs.sink = this.setConfigValuesGQL.mutate({
      configs: [{ key: row.config.key, value: serializedValue }],
    }).pipe(finalize(() => this.saving = false)).subscribe({
      next: () => {
        this.cancelEditing();
        this.notify.success('Document setting updated');
        this.rows = this.rows.map((currentRow) => {
          if (currentRow.config.key !== row.config.key) { return currentRow; }

          return {
            ...currentRow,
            config: { ...currentRow.config, value: serializedValue },
            ...(field === 'templateId'
              ? {
                pandadocTemplateId: savedValue || null,
                pandadocTemplateUrl: savedValue
                  ? `https://app.pandadoc.com/a/#/templates/${encodeURIComponent(savedValue)}`
                  : null,
              }
              : { aegisTemplateUrl: savedValue || null }),
          };
        });
      },
      error: (error) => this.notify.apolloError('Failed to update document setting', error),
    });
  }

  handleEditorKeydown(event: KeyboardEvent, row: DocumentSettingsQuery['documentSettings'][number], field: EditableConfigField) {
    if (event.key === 'Enter') {
      this.save(row, field);
    } else if (event.key === 'Escape') {
      this.cancelEditing();
    }
  }

  engineSwitchDisabledReason(row: DocumentSettingsQuery['documentSettings'][number]) {
    if (this.updatingEngineKeys.has(row.config.key)) { return 'Updating document engine'; }
    if (row.engine === 'aegis' && !row.pandadocTemplateId) { return 'Set a PandaDoc template ID before switching'; }
    if (row.engine === 'pandadoc' && !row.aegisTemplateUrl) { return 'Set an Aegis template URL before switching'; }
    return '';
  }

  engineSwitchTooltip(row: DocumentSettingsQuery['documentSettings'][number]) {
    return this.engineSwitchDisabledReason(row)
      || `Switch to ${row.engine === 'aegis' ? 'PandaDoc' : 'Aegis'}`;
  }

  switchEngine(row: DocumentSettingsQuery['documentSettings'][number]) {
    if (this.engineSwitchDisabledReason(row)) { return; }

    let configValue: Record<string, unknown>;
    try {
      configValue = JSON.parse(row.config.value || '') as Record<string, unknown>;
    } catch {
      this.notify.error('Failed to update document engine', 'The current configuration is not valid JSON.');
      return;
    }

    const engine: DocumentEngine = row.engine === 'aegis' ? 'pandadoc' : 'aegis';
    configValue.engine = engine;
    const serializedValue = JSON.stringify(configValue);
    this.updatingEngineKeys = new Set(this.updatingEngineKeys).add(row.config.key);

    this.subs.sink = this.setConfigValuesGQL.mutate({
      configs: [{ key: row.config.key, value: serializedValue }],
    }).pipe(finalize(() => {
      const updatingEngineKeys = new Set(this.updatingEngineKeys);
      updatingEngineKeys.delete(row.config.key);
      this.updatingEngineKeys = updatingEngineKeys;
    })).subscribe({
      next: () => {
        this.rows = this.rows.map((currentRow) => currentRow.config.key === row.config.key
          ? {
            ...currentRow,
            engine,
            config: { ...currentRow.config, value: serializedValue },
          }
          : currentRow);
        this.notify.success(`Document engine switched to ${engine === 'aegis' ? 'Aegis' : 'PandaDoc'}`);
      },
      error: (error) => this.notify.apolloError('Failed to update document engine', error),
    });
  }

  private setDatesFromPeriod(period: AdvancedPeriodOptions) {
    this.filterForm.controls.customPeriod.setValue(this.periodService.getPeriodAsDateArray(period));
  }

  private loadAegisTemplates() {
    this.subs.sink = this.http.get<AegisTemplate[]>(`${functionsBaseUrl()}/aegis-templates.json`).subscribe({
      next: (templates) => {
        this.aegisTemplates = templates
          .filter((template) => typeof template?.name === 'string' && typeof template?.url === 'string' && template.url)
          .sort((left, right) => left.name.localeCompare(right.name));
        this.filteredAegisTemplates = this.aegisTemplates.map((template) => template.url);
      },
      error: (error) => this.notify.error('Failed to load Aegis templates', error.message),
    });
  }

  private migrationRate(aegisCount: number, pandadocCount: number) {
    const total = aegisCount + pandadocCount;
    return total > 0 ? aegisCount / total : null;
  }
}
